"use client"

import { FormikProvider, type FormikErrors, useFormik } from "formik"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormInput, FormTextarea } from "@/components/form-inputs"
import { useEnterpriseInquiryMutation } from "@/hooks/mutations/enterprise-inquiry"
import type { EnterpriseInquiryInput } from "@/server-actions/enterprise-inquiry"
import type { BillingChannel } from "@/lib/billing/constants"
import { useTranslations } from "next-intl"


interface EnterpriseContactDialogProps {
  open: boolean
  channel: BillingChannel
  onOpenChange: (open: boolean) => void
  onSubmitted: () => void
}

export function EnterpriseContactDialog({
  open,
  channel,
  onOpenChange,
  onSubmitted,
}: EnterpriseContactDialogProps) {
  const tUi = useTranslations("Auth.EnterpriseContactDialog")
  const tCommonUi = useTranslations("Common")

  const mutation = useEnterpriseInquiryMutation()
  const formik = useFormik<EnterpriseInquiryInput>({
    initialValues: {
      name: "",
      company: "",
      email: "",
      needs: "",
      signupChannel: channel,
    },
    enableReinitialize: true,
    validate: validateEnterpriseInquiry,
    onSubmit: async (values, helpers) => {
      try {
        await mutation.mutateAsync({
          ...values,
          setErrors: (errors) => {
            helpers.setErrors(errors as FormikErrors<EnterpriseInquiryInput>)
          },
        })
        helpers.resetForm()
        onSubmitted()
      } catch {
        // The mutation displays the delivery error and maps field errors.
      }
    },
  })

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !mutation.isPending) {
      formik.resetForm()
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle><span key={tUi("contactSales")}>{tUi("contactSales")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("tellUsWhatYouNeedAndOurTeam")}>{tUi("tellUsWhatYouNeedAndOurTeam")}</span></DialogDescription>
        </DialogHeader>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <FormInput
              name="name"
              label={tUi("name")}
              placeholder={tUi("yourName")}
              maxLength={120}
              required
              disabled={mutation.isPending}
            />
            <FormInput
              name="company"
              label={tUi("companyOptional")}
              placeholder={tUi("companyName")}
              maxLength={160}
              disabled={mutation.isPending}
            />
            <FormInput
              name="email"
              label={tUi("email")}
              type="email"
              placeholder={"you@company.com"}
              maxLength={254}
              required
              disabled={mutation.isPending}
            />
            <FormTextarea
              name="needs"
              label={tUi("explainYourNeeds")}
              placeholder={tUi("tellUsAboutYourTeamExpectedUsageIntegrations")}
              rows={5}
              maxLength={5_000}
              showLength
              required
              disabled={mutation.isPending}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={mutation.isPending}
              >
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                <span><span key={tUi("sendRequest")}>{tUi("sendRequest")}</span></span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function validateEnterpriseInquiry(
  values: EnterpriseInquiryInput,
): FormikErrors<EnterpriseInquiryInput> {
  const errors: FormikErrors<EnterpriseInquiryInput> = {}

  if (!values.name.trim()) errors.name = "Name is required."
  if (!values.email.trim()) {
    errors.email = "Email is required."
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = "Enter a valid email address."
  }
  if (!values.needs.trim()) errors.needs = "Explain your needs."

  return errors
}
